import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface CasesClientSearchState {
  selectedCaseForDetails: Record<string, unknown> | null;
  viewClientsActiveCases: Record<string, unknown> | null;
  createNewCaseForSelectedClient: boolean;
  navigatedToShowCaseFrom: string | null;
  navigatedToShowClientDetailsFrom: string | null;
  selectedCaseForEdit: Record<string, unknown> | null;
  selectedClientShowinUi: string | number | null;
  searchStringIs: string | null;
  drawerOpenClose: boolean | null;
  alreadyLoaded: boolean;
  allTagsAsync: Record<string, unknown>[];
  profilePictureLocal: string | null;
  firstLoadDone: boolean;
}

const initialState: CasesClientSearchState = {
  selectedCaseForDetails: null,
  viewClientsActiveCases: null,
  createNewCaseForSelectedClient: false,
  navigatedToShowCaseFrom: null,
  navigatedToShowClientDetailsFrom: null,
  selectedCaseForEdit: null,
  selectedClientShowinUi: null,
  searchStringIs: null,
  drawerOpenClose: null,
  alreadyLoaded: false,
  allTagsAsync: [],
  profilePictureLocal: null,
  firstLoadDone: false,
};

const casesClientSearchSlice = createSlice({
  name: "casesClientSearch",
  initialState,
  reducers: {
    setSelectedCaseForDetails: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.selectedCaseForDetails = action.payload;
    },
    setViewClientsActiveCases: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.viewClientsActiveCases = action.payload;
    },
    setCreateNewCaseForSelectedClient: (state, action: PayloadAction<boolean>) => {
      state.createNewCaseForSelectedClient = action.payload;
    },
    setNavigatedToShowCaseFrom: (state, action: PayloadAction<string | null>) => {
      state.navigatedToShowCaseFrom = action.payload;
    },
    setNavigatedToShowClientDetailsFrom: (state, action: PayloadAction<string | null>) => {
      state.navigatedToShowClientDetailsFrom = action.payload;
    },
    setSelectedCaseForEdit: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.selectedCaseForEdit = action.payload;
    },
    setSelectedClientShowinUi: (state, action: PayloadAction<string | number | null>) => {
      state.selectedClientShowinUi = action.payload;
    },
    setSearchStringIs: (state, action: PayloadAction<string | null>) => {
      state.searchStringIs = action.payload;
    },
    setDrawerOpenClose: (state, action: PayloadAction<boolean | null>) => {
      state.drawerOpenClose = action.payload;
    },
    setAlreadyLoaded: (state, action: PayloadAction<boolean>) => {
      state.alreadyLoaded = action.payload;
    },
    setAllTagsAsync: (state, action: PayloadAction<Record<string, unknown>[]>) => {
      state.allTagsAsync = action.payload;
    },
    setProfilePictureLocal: (state, action: PayloadAction<string | null>) => {
      state.profilePictureLocal = action.payload;
    },
    setFirstLoadDone: (state, action: PayloadAction<boolean>) => {
      state.firstLoadDone = action.payload;
    },
    clearCasesClientSearch: () => initialState,
    clearViewClientsAndCreateNewCase: (state) => {
      state.viewClientsActiveCases = null;
      state.createNewCaseForSelectedClient = false;
    },
  },
});

export const {
  setSelectedCaseForDetails,
  setViewClientsActiveCases,
  setCreateNewCaseForSelectedClient,
  setNavigatedToShowCaseFrom,
  setNavigatedToShowClientDetailsFrom,
  setSelectedCaseForEdit,
  setSelectedClientShowinUi,
  setSearchStringIs,
  setDrawerOpenClose,
  setAlreadyLoaded,
  setAllTagsAsync,
  setProfilePictureLocal,
  setFirstLoadDone,
  clearCasesClientSearch,
  clearViewClientsAndCreateNewCase,
} = casesClientSearchSlice.actions;

export const selectSearchStringIs = (state: { casesClientSearch?: CasesClientSearchState }) =>
  state.casesClientSearch?.searchStringIs ?? null;

export const selectSelectedClientShowinUi = (state: {
  casesClientSearch?: CasesClientSearchState;
}) => state.casesClientSearch?.selectedClientShowinUi ?? null;

export const selectSelectedCaseForDetails = (state: {
  casesClientSearch?: CasesClientSearchState;
}) => state.casesClientSearch?.selectedCaseForDetails ?? null;

export const selectSelectedCaseForEdit = (state: { casesClientSearch?: CasesClientSearchState }) =>
  state.casesClientSearch?.selectedCaseForEdit ?? null;

export const selectViewClientsActiveCases = (state: {
  casesClientSearch?: CasesClientSearchState;
}) => state.casesClientSearch?.viewClientsActiveCases ?? null;

export const selectCreateNewCaseForSelectedClient = (state: {
  casesClientSearch?: CasesClientSearchState;
}) => state.casesClientSearch?.createNewCaseForSelectedClient ?? false;

export const selectDrawerOpenClose = (state: { casesClientSearch?: CasesClientSearchState }) =>
  state.casesClientSearch?.drawerOpenClose ?? null;

export const selectCasesClientSearchAlreadyLoaded = (state: {
  casesClientSearch?: CasesClientSearchState;
}) => state.casesClientSearch?.alreadyLoaded ?? false;

export const selectAllTagsAsync = (state: { casesClientSearch?: CasesClientSearchState }) =>
  state.casesClientSearch?.allTagsAsync ?? [];

export const selectProfilePictureLocal = (state: { casesClientSearch?: CasesClientSearchState }) =>
  state.casesClientSearch?.profilePictureLocal ?? null;

export const selectCasesClientSearchFirstLoadDone = (state: {
  casesClientSearch?: CasesClientSearchState;
}) => state.casesClientSearch?.firstLoadDone ?? false;

export default casesClientSearchSlice.reducer;
